/**
 * Create User Page
 *
 * Form for creating a new platform user.
 * Only accessible by ADMIN and SADMIN roles.
 */

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { ArrowLeft, Save, Eye, EyeOff } from 'lucide-react';

import {
  Button,
  Input,
  Select,
  MultiSelect,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
} from '@/components/common';
import { userService, ApiError } from '@/api/services';
import type { CreateUserRequest, Gender, OrgUserType } from '@/api/types';
import { useToast } from '@/hooks/useToast';
import { useRoleOptions } from '@/hooks/useRoleOptions';

// ============================================
// Form Schema
// ============================================

const createUserSchema = z.object({
  username: z
    .string()
    .min(3, 'Username must be at least 3 characters')
    .max(50, 'Username must be less than 50 characters')
    .regex(/^[a-zA-Z0-9_]+$/, 'Username can only contain letters, numbers, and underscores'),
  password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number'),
  confirmPassword: z.string(),
  firstName: z.string().min(1, 'First name is required'),
  lastName: z.string().optional(),
  email: z.string().email('Invalid email address'),
  mobile: z.string().min(10, 'Mobile number must be at least 10 digits'),
  gender: z.enum(['MALE', 'FEMALE', 'OTHER']).optional(),
  orgUserType: z.enum(['SADMIN', 'ADMIN', 'STAFF'], {
    error: 'User type is required',
  }),
  roles: z.array(z.string()).min(1, 'At least one role is required'),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ['confirmPassword'],
});

type CreateUserFormData = z.infer<typeof createUserSchema>;

// ============================================
// Role Options
// ============================================

const GENDER_OPTIONS = [
  { value: 'MALE', label: 'Male' },
  { value: 'FEMALE', label: 'Female' },
  { value: 'OTHER', label: 'Other' },
];

const USER_TYPE_OPTIONS = [
  { value: 'SADMIN', label: 'Super Admin' },
  { value: 'ADMIN', label: 'Admin' },
  { value: 'STAFF', label: 'Operations Staff' },
];

// ============================================
// Page Component
// ============================================

export function CreateUserPage() {
  const navigate = useNavigate();
  const toast = useToast();
  // Roles are loaded from the RBAC catalog (GET /api/rbac/roles).
  const { roleOptions } = useRoleOptions();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm<CreateUserFormData>({
    resolver: zodResolver(createUserSchema),
    defaultValues: {
      roles: [],
    },
  });

  const selectedRoles = watch('roles');
  const selectedGender = watch('gender');
  const selectedUserType = watch('orgUserType');

  const onSubmit = async (data: CreateUserFormData) => {
    setIsSubmitting(true);
    setFormError(null);

    try {
      const request: CreateUserRequest = {
        username: data.username,
        password: data.password,
        firstName: data.firstName,
        lastName: data.lastName,
        email: data.email,
        mobile: data.mobile,
        gender: data.gender as Gender | undefined,
        orgUserType: data.orgUserType as OrgUserType | undefined,
        listOfRoles: data.roles,
      };

      await userService.createUser(request);
      toast.success('User created successfully', { duration: 4000 });
      navigate('/users');
    } catch (error) {
      console.error('Failed to create user:', error);

      if (error instanceof ApiError) {
        setFormError(error.showMessage);
      } else if (error instanceof Error) {
        setFormError(error.message || 'Failed to create user. Please try again.');
      } else {
        setFormError('Failed to create user. Please try again.');
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" onClick={() => navigate('/users')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-2xl font-bold text-foreground dark:text-foreground-dark">
            Create User
          </h1>
          <p className="mt-1 text-sm text-foreground-muted dark:text-foreground-dark-muted">
            Add a new platform administrator account
          </p>
        </div>
      </div>

      {/* Form */}
      <form onSubmit={handleSubmit(onSubmit)}>
        <Card>
          <CardHeader
            title="User Information"
            description="Fill in the details for the new user"
          />
          <CardBody className="space-y-6">
            {/* Form Error Message */}
            {formError && (
              <div className="rounded-md bg-danger/10 border border-danger/20 p-4">
                <p className="text-sm text-danger dark:text-danger-dark">{formError}</p>
              </div>
            )}

            {/* Username & Email */}
            <div className="grid gap-6 md:grid-cols-2">
              <Input
                label="Username"
                placeholder="Enter username"
                required
                error={errors.username?.message}
                {...register('username')}
              />
              <Input
                label="Email"
                type="email"
                placeholder="Enter email address"
                required
                error={errors.email?.message}
                {...register('email')}
              />
            </div>

            {/* Password */}
            <div className="grid gap-6 md:grid-cols-2">
              <Input
                label="Password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter password"
                required
                error={errors.password?.message}
                rightElement={
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="focus:outline-none"
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                }
                {...register('password')}
              />
              <Input
                label="Confirm Password"
                type={showConfirmPassword ? 'text' : 'password'}
                placeholder="Confirm password"
                required
                error={errors.confirmPassword?.message}
                rightElement={
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="focus:outline-none"
                  >
                    {showConfirmPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                }
                {...register('confirmPassword')}
              />
            </div>

            {/* Name */}
            <div className="grid gap-6 md:grid-cols-2">
              <Input
                label="First Name"
                placeholder="Enter first name"
                required
                error={errors.firstName?.message}
                {...register('firstName')}
              />
              <Input
                label="Last Name"
                placeholder="Enter last name"
                error={errors.lastName?.message}
                {...register('lastName')}
              />
            </div>

            {/* Mobile & Gender */}
            <div className="grid gap-6 md:grid-cols-2">
              <Input
                label="Mobile Number"
                placeholder="Enter mobile number"
                required
                error={errors.mobile?.message}
                {...register('mobile')}
              />
              <Select
                label="Gender"
                placeholder="Select gender"
                options={GENDER_OPTIONS}
                value={selectedGender}
                onChange={(value) => setValue('gender', value as Gender)}
              />
            </div>

            {/* User Type */}
            <div className="grid gap-6 md:grid-cols-2">
              <Select
                label="User Type"
                placeholder="Select user type"
                required
                options={USER_TYPE_OPTIONS}
                value={selectedUserType}
                onChange={(value) =>
                  setValue('orgUserType', value as OrgUserType, { shouldValidate: true })
                }
                error={!!errors.orgUserType}
                errorMessage={errors.orgUserType?.message}
              />
            </div>

            {/* Roles */}
            <MultiSelect
              label="Roles"
              placeholder="Select roles"
              required
              options={roleOptions}
              value={selectedRoles}
              onChange={(values) => setValue('roles', values, { shouldValidate: true })}
              errorMessage={errors.roles?.message}
            />
          </CardBody>

          <CardFooter className="justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={() => navigate('/users')}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              loading={isSubmitting}
              leftIcon={<Save className="h-4 w-4" />}
            >
              Create User
            </Button>
          </CardFooter>
        </Card>
      </form>
    </div>
  );
}

export default CreateUserPage;
